import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import AccountSetupStepper from '../components/AccountSetupStepper';
import AccountDetailsStep from '../components/steps/AccountDetailsStep';
import SubscriptionStep from '../components/steps/SubscriptionStep';
import TermsStep from '../components/steps/TermsStep';
import AccountCompletionStep from '../components/steps/AccountCompletionStep';
import PaymentStep from '../components/steps/PaymentStep';
import OfferSummaryCard from '../components/OfferSummaryCard';
import config from '../config';
import Helper from '../helpers';
import './CreateAccount.css';

const baseUrl = config.apiBaseUrl;

// ─────────────────────────────────────────────────────────────────────────────
// MemberSignup
// Handles two paths:
//   • /signup/venue/:venueKey        – direct venue member signup
//   • /signup/invite/:inviteCode     – invite-link offer signup
//
// Steps: Account Details → Subscription → Terms & Conditions → Payment → Complete
// The Subscription step is skipped for invite-offer (subscription is pre-set).
// ─────────────────────────────────────────────────────────────────────────────

const INVITE_MODE = 'invite-offer';
const MEMBER_MODE = 'venue-member';

const MEMBER_STEPS = ['Account Details', 'Subscription', 'Terms & Conditions', 'Payment', 'Complete'];
const INVITE_STEPS = ['Account Details', 'Terms & Conditions', 'Payment', 'Complete'];

const MemberSignup = () => {
    const { venueKey, inviteCode } = useParams();
    const navigate = useNavigate();

    const mode = inviteCode ? INVITE_MODE : MEMBER_MODE;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [steps, setSteps] = useState([]);

    const [formData, setFormData] = useState({
        email: '', firstName: '', surname: '', password: '', confirmPassword: '',
        selectedSubscription: ''
    });

    const [offerDetails, setOfferDetails] = useState(null);
    const [venueDetails, setVenueDetails] = useState(null);
    const [subscriptionOptions, setSubscriptionOptions] = useState([]);
    const [noSubscriptionsAvailable, setNoSubscriptionsAvailable] = useState(false);

    // Terms
    const [whsTermsAccepted, setWhsTermsAccepted] = useState(false);
    const [venueTermsAccepted, setVenueTermsAccepted] = useState(false);

    // Set when the person pressed cancel on the GoCardless page and was sent back to this invitation
    const cancelledPayment = new URLSearchParams(window.location.search).get('cancelled') === 'true';
    // Set when sign up finds an account for this email already exists (for example they stopped part way)
    const [accountExists, setAccountExists] = useState(false);

    // An account already exists at this point, so the way to finish is to log in and accept the offer
    const continueAsMember = () => {
        navigate(`/login?returnUrl=${encodeURIComponent(`/accept-subscription-offer/${inviteCode}`)}`);
    };

    // Payment
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentError, setPaymentError] = useState('');
    const [loadingMessage, setLoadingMessage] = useState('');

    // ── Init ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setError('');
            try {
                if (mode === INVITE_MODE) {
                    await initInviteMode();
                } else {
                    await initMemberMode();
                }
            } catch (err) {
                setError(err.message || 'Failed to load signup. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venueKey, inviteCode]);

    const initMemberMode = async () => {
        // Fetch venue details
        const vRes = await fetch(`${baseUrl}/api/Venue/public/${venueKey}`);
        if (!vRes.ok) throw new Error('Venue not found. Please check the link and try again.');
        const venue = await vRes.json();
        setVenueDetails(venue);

        // Fetch venue's ShowOnSignup subscriptions
        const sRes = await fetch(`${baseUrl}/api/Subscription/public/${venueKey}`);
        if (!sRes.ok) throw new Error('Failed to load venue membership options.');
        const sData = await sRes.json();
        const subs = (sData.status === 'success' && sData.subscriptions) ? sData.subscriptions : [];
        setSubscriptionOptions(subs);
        if (subs.length === 0) setNoSubscriptionsAvailable(true);

        setSteps(MEMBER_STEPS);
        setFormData({ email: '', firstName: '', surname: '', password: '', confirmPassword: '', selectedSubscription: '' });
    };

    const initInviteMode = async () => {
        // Someone who is already logged in has a profile. Send them straight to accepting the offer
        // instead of showing them a sign up form.
        const token = await Helper.getCookie('wh-jwt');
        const stillLoggedIn = token && Helper.parseJwt(token)?.exp > Date.now() / 1000;
        if (stillLoggedIn) {
            navigate(`/accept-subscription-offer/${inviteCode}`, { replace: true });
            return;
        }

        const res = await fetch(`${baseUrl}/api/SubscriptionOffer/details/${inviteCode}`);
        if (!res.ok) throw new Error('Invalid or expired invite link.');
        const data = await res.json();

        const status = data.status || data.Status;
        const offer = data.offer || data.Offer;
        if (status !== 'success' || !offer) throw new Error('Invalid invite code.');
        if (offer.isExpired) throw new Error('This invitation has expired.');
        if (!offer.isValid) throw new Error('This invitation is no longer valid.');

        setOfferDetails(offer);
        setVenueDetails({ venueKey: offer.venueKey, venueName: offer.venueName });

        setSubscriptionOptions([{
            subscriptionId: offer.subscriptionId || 'custom',
            name: offer.subscriptionName,
            price: offer.cycleCost,
            tokenAmount: offer.tokens,
            frequency: offer.cycleType,
            cycleLength: offer.cycleLength
        }]);

        setSteps(INVITE_STEPS);
        setFormData({
            email: offer.recipientEmail || '',
            firstName: '', surname: '', password: '', confirmPassword: '',
            // A pay as you go invite has no plan and no Direct Debit, so it must not go down the custom plan path
            selectedSubscription: offer.isPayAsYouGo ? 'payg' : (offer.subscriptionId || 'custom')
        });
    };

    // ── Validation ───────────────────────────────────────────────────────────
    const validateCurrentStep = () => {
        setError('');
        const stepName = steps[currentStep];

        switch (stepName) {
            case 'Account Details': {
                if (!formData.email || !/^[^@]+@[^@]+\.[^@]+$/.test(formData.email)) {
                    setError('Please enter a valid email address'); return false;
                }
                if (!formData.firstName) { setError('Please enter your first name'); return false; }
                if (!formData.surname) { setError('Please enter your surname'); return false; }
                if (!formData.password || formData.password.length < 8) {
                    setError('Password must be at least 8 characters'); return false;
                }
                if (formData.password.length > 70) {
                    setError('Password must be 70 characters or fewer'); return false;
                }
                if (formData.password !== formData.confirmPassword) {
                    setError('Passwords do not match'); return false;
                }
                return true;
            }
            case 'Subscription': {
                if (!noSubscriptionsAvailable && !formData.selectedSubscription) {
                    setError('Please select a subscription plan'); return false;
                }
                return true;
            }
            case 'Terms & Conditions': {
                if (!whsTermsAccepted) {
                    setError('Please accept the WorldsHighStreet terms and conditions'); return false;
                }
                if (!venueTermsAccepted) {
                    setError('Please accept the venue terms and conditions'); return false;
                }
                return true;
            }
            default:
                return true;
        }
    };

    // ── Navigation ───────────────────────────────────────────────────────────
    const handleNext = async () => {
        if (!validateCurrentStep()) return;
        if (steps[currentStep + 1] === 'Payment') {
            await initiatePaymentFlow();
            return;
        }
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 0));

    // ── Payment ──────────────────────────────────────────────────────────────
    const initiatePaymentFlow = async () => {
        try {
            setPaymentStatus('processing');
            setCurrentStep(prev => prev + 1);

            // Step 1 — create user account
            setLoadingMessage('Creating your account...');
            const resolvedVenueKey = mode === INVITE_MODE
                ? (venueDetails?.venueKey || offerDetails?.venueKey)
                : venueKey;

            const signupPayload = {
                venueKey: resolvedVenueKey,
                emailAddress: formData.email,
                firstName: formData.firstName,
                surname: formData.surname,
                password: formData.password,
                oiriginTrace: mode === INVITE_MODE ? `invite-${inviteCode}` : 'member-signup'
            };

            const userRes = await fetch(`${baseUrl}/api/Users/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(signupPayload)
            });

            if (!userRes.ok) {
                const errData = await userRes.json().catch(() => ({}));
                if (mode === INVITE_MODE && (errData.code === 'user_already_exists' || errData.code === 'account_exists')) {
                    setAccountExists(true);
                }
                throw new Error(errData.message || errData.Message || 'Failed to create account');
            }

            const userData = await userRes.json();
            const userId = userData.id;
            if (!userId) throw new Error('Failed to create user account – no user ID returned');

            // PAYG — no mandate needed
            const rawSub = formData.selectedSubscription;
            if (!rawSub || rawSub === 'payg') {
                setPaymentStatus('complete');
                setCurrentStep(prev => prev + 1);
                return;
            }

            // Step 2 — setup GoCardless mandate
            setLoadingMessage('Setting up payment...');

            const subscriptionId = (rawSub && rawSub !== 'custom')
                ? (typeof rawSub === 'string' ? parseInt(rawSub, 10) : rawSub)
                : null;

            const paymentRes = await fetch(`${baseUrl}/api/PaymentAdmin/setupuserpayment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    venueKey: resolvedVenueKey,
                    userId,
                    subscriptionId,
                    offerId: null,
                    mode,
                    inviteCode: inviteCode || null
                })
            });

            if (!paymentRes.ok) {
                const errData = await paymentRes.json().catch(() => ({}));
                throw new Error(errData.message || errData.Message || 'Failed to setup payment');
            }

            const paymentData = await paymentRes.json();
            setLoadingMessage('Redirecting to payment provider...');
            const mandateUrl = paymentData.mandateUrl || paymentData.MandateUrl;
            if (mandateUrl) {
                window.location.href = mandateUrl;
            } else {
                throw new Error('Payment URL not received from server');
            }
        } catch (err) {
            console.error('Payment flow error:', err);
            setPaymentStatus('failed');
            setPaymentError(err.message || 'Failed to initiate payment setup');
            setLoadingMessage('');
        }
    };

    // ── Render step content ───────────────────────────────────────────────────
    const renderStepContent = () => {
        const stepName = steps[currentStep];
        const resolvedVenueKey = mode === INVITE_MODE
            ? (venueDetails?.venueKey || offerDetails?.venueKey)
            : venueKey;

        switch (stepName) {
            case 'Account Details':
                return (
                    <AccountDetailsStep
                        formData={formData}
                        setFormData={setFormData}
                        emailValidationText={error}
                        passwordValidationText=""
                        disableEmail={mode === INVITE_MODE && !!offerDetails?.recipientEmail}
                    />
                );

            case 'Subscription':
                return (
                    <SubscriptionStep
                        subscriptions={subscriptionOptions}
                        selectedSubscription={formData.selectedSubscription}
                        setSelectedSubscription={(id) => setFormData({ ...formData, selectedSubscription: id })}
                        venueName={venueDetails?.VenueName || venueDetails?.venueName}
                    />
                );

            case 'Terms & Conditions':
                return (
                    <TermsStep
                        worldHighStreetTermsAccepted={whsTermsAccepted}
                        setWorldHighStreetTermsAccepted={setWhsTermsAccepted}
                        venueTermsAccepted={venueTermsAccepted}
                        setVenueTermsAccepted={setVenueTermsAccepted}
                        venueKey={resolvedVenueKey}
                    />
                );

            case 'Payment':
                return (
                    <div>
                        {loadingMessage && (
                            <div className="mb-6 text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                                <p className="text-emerald-400 text-lg font-medium">{loadingMessage}</p>
                            </div>
                        )}
                        <PaymentStep status={paymentStatus} error={paymentError} />
                        {accountExists && (
                            <div className="mt-4 text-center">
                                <p className="text-sm text-gray-300 mb-3">
                                    You already have a WorldsHighStreet account with this email. Log in with your usual password to carry on with this offer. You do not need a new account.
                                </p>
                                <button onClick={continueAsMember} className="btn-primary">
                                    Log in and continue
                                </button>
                            </div>
                        )}
                    </div>
                );

            case 'Complete':
                return <AccountCompletionStep />;

            default:
                return null;
        }
    };

    // ── Loading / Error screens ───────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-dark-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    if (error && currentStep === 0 && !formData.email) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-dark-900 p-4">
                <div className="card-luxury p-8 max-w-md w-full text-center">
                    <div className="text-performance-pulse mb-4">
                        <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-display font-bold text-accent-platinum mb-4">
                        Unable to Load Signup
                    </h2>
                    <p className="text-emerald-600/70 font-body mb-6">{error}</p>
                    <button onClick={() => navigate('/')} className="btn-outline">
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    const venueName = venueDetails?.VenueName || venueDetails?.venueName;

    // The person this invitation is for already has a WorldsHighStreet profile (for example from another
    // venue). They do not create an account. They log in and take up the offer with the profile they have.
    if (mode === INVITE_MODE && offerDetails?.recipientHasAccount) {
        return (
            <div className="min-h-screen bg-dark-900 py-8 px-4">
                <div className="max-w-2xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl sm:text-4xl font-display font-bold text-emerald-500 mb-2">Welcome back</h1>
                        <p className="text-emerald-600/70 font-body">
                            {venueName ? `${venueName} has invited you with a special offer` : 'You have been invited with a special offer'}
                        </p>
                    </div>

                    <OfferSummaryCard offer={offerDetails} />

                    <div className="card-luxury p-6 sm:p-8 text-center" data-testid="existing-account-panel">
                        <p className="text-gray-200 mb-2">You already have a WorldsHighStreet profile, so you do not need to create a new account.</p>
                        <p className="text-sm text-gray-400 mb-5">
                            Log in with your usual email and password, then accept the offer and set up your Direct Debit.
                        </p>
                        <button onClick={continueAsMember} className="btn-primary">
                            Log in and continue
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dark-900 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl sm:text-4xl font-display font-bold text-emerald-500 mb-2">
                        {mode === INVITE_MODE ? "You're Invited!" : `Join ${venueName || 'this Venue'}`}
                    </h1>
                    <p className="text-emerald-600/70 font-body">
                        {mode === INVITE_MODE
                            ? `${venueName} has invited you with a special offer`
                            : 'Create your account and start your fitness journey'}
                    </p>

                    {/* Venue info banner */}
                    {venueName && (
                        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-900/20 border border-emerald-700/40 rounded-full">
                            <span className="text-emerald-400 text-sm font-medium">{venueName}</span>
                        </div>
                    )}
                </div>

                {/* WorldsHighStreet context note */}
                <div className="mb-6 p-4 bg-gray-800/50 border border-gray-700 rounded-xl">
                    <p className="text-gray-400 text-sm text-center">
                        WorldsHighStreet is the platform that powers {venueName ? `${venueName}'s` : "this venue's"} memberships and bookings.
                    </p>
                    <p className="text-gray-400 text-sm text-center">
                        You'll be asked to accept both our platform terms and {venueName ? `${venueName}'s` : "the venue's"} own terms before completing signup.
                    </p>
                </div>

                {/* They pressed cancel on the GoCardless page: back at the start, with a clear way to carry on */}
                {mode === INVITE_MODE && cancelledPayment && steps[currentStep] !== 'Complete' && (
                    <div className="mb-6 p-4 rounded-xl border border-amber-500/40 bg-amber-900/20">
                        <p className="text-amber-200 font-semibold mb-1">You stopped before finishing your Direct Debit set up</p>
                        <p className="text-sm text-gray-300">
                            Nothing has been taken. Your invitation is still here. If you already created your account, log in to carry on where you left off.
                        </p>
                        <button onClick={continueAsMember} className="btn-primary mt-3">
                            Log in and continue
                        </button>
                    </div>
                )}

                {/* Someone who already has a profile can log in and come straight back to this offer */}
                {mode === INVITE_MODE && steps[currentStep] === 'Account Details' && (
                    <p className="mb-4 text-center text-sm text-gray-300" data-testid="already-have-account">
                        Already have a WorldsHighStreet account?{' '}
                        <button type="button" onClick={continueAsMember} className="text-emerald-400 underline font-semibold">
                            Log in and continue
                        </button>
                    </p>
                )}

                {/* The deal this invite link is for, so the person sees the price, cycle and first payment before paying */}
                {mode === INVITE_MODE && steps[currentStep] !== 'Complete' && (
                    <OfferSummaryCard offer={offerDetails} />
                )}

                {/* Stepper */}
                <div className="mb-8">
                    <AccountSetupStepper currentStep={currentStep} steps={steps} />
                </div>

                {/* Step content */}
                <div className="card-luxury p-6 sm:p-8 mb-6">
                    {renderStepContent()}

                    {error && (
                        <div className="mt-4 p-4 glass-luxury rounded-xl border border-performance-pulse/30 bg-gradient-to-br from-performance-pulse/5 to-luxury-obsidian/80">
                            <div className="flex items-start gap-3">
                                <svg className="h-5 w-5 text-performance-pulse flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <p className="text-sm text-performance-pulse font-body font-medium">{error}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation buttons */}
                {steps[currentStep] !== 'Payment' && steps[currentStep] !== 'Complete' && (
                    <div className="flex justify-between gap-4">
                        {currentStep > 0 && (
                            <button onClick={handleBack} className="btn-outline flex items-center gap-2">
                                <ArrowLeftIcon className="h-5 w-5" />
                                Back
                            </button>
                        )}
                        <button onClick={handleNext} className="btn-primary flex items-center gap-2 ml-auto">
                            {steps[currentStep + 1] === 'Payment' ? 'Continue to Payment' : 'Next'}
                            <ArrowRightIcon className="h-5 w-5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MemberSignup;



